import { RoundedBox } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import {
    memo,
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import type {
    Material,
    Mesh,
    Scene,
    WebGLRenderer,
} from 'three';
import type {
    ModelViewerPedestalLayout,
    ModelViewerPedestalMaterial,
} from '../Pedestal';
import PedestalGlassMaterial from './PedestalGlassMaterial';
import PedestalMarbleMaterial from './PedestalMarbleMaterial';
import { deriveStudioPedestalColor } from './PedestalColor';
import {
    createBeveledCylinderProfile,
    getStudioPedestalBodyLayout,
} from './PedestalGeometry';
import PedestalGroundShadow from './PedestalGroundShadow';
import PedestalReflectionProxy from './PedestalReflectionProxy';
import { STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY } from './config';
import { renderReadiness } from '../../Rendering/browserReadiness';

interface Props {
    cycloramaColor: string
    layout: ModelViewerPedestalLayout
    material: ModelViewerPedestalMaterial
    transmissionRevision?: number
}

/**
 * Studio support prop. The body extends down from the centered product's
 * existing bottom and casts a live camera-relative shadow onto the lowered
 * cyclorama floor. Product contact shading is drawn separately on its top.
 */
export default memo(function Pedestal({
    cycloramaColor,
    layout,
    material,
    transmissionRevision,
}:Props) {
    const invalidate = useThree(state => state.invalidate);
    const pedestalRef = useRef<Mesh>(null);
    const pendingTransmissionRevisionRef = useRef(transmissionRevision);
    const body = getStudioPedestalBodyLayout(layout, material);
    const pedestalColor = useMemo(
        () => deriveStudioPedestalColor(cycloramaColor),
        [cycloramaColor],
    );
    const beveledCylinderProfile = useMemo(() => (
        createBeveledCylinderProfile(
            layout.radius,
            body.height,
            body.cylinderBevel,
        )
    ), [
        body.cylinderBevel,
        body.height,
        layout.radius,
    ]);
    // Glass participates in both the floor mirror and its own transmission
    // capture. Hide its color/depth writes during the mirror pass to prevent a
    // recursive pedestal reflection and restore them for the main framebuffer.
    const prepareBodyRender = useCallback((
        _renderer:WebGLRenderer,
        scene:Scene,
    ) => {
        if(material !== 'glass') return;

        const bodyMaterial = pedestalRef.current?.material as (
            Material | undefined
        );
        if(!bodyMaterial) return;

        const reflectionCaptureActive = (
            scene.userData[
                STUDIO_REFLECTION_CAPTURE_ACTIVE_USER_DATA_KEY
            ] === true
        );
        bodyMaterial.colorWrite = !reflectionCaptureActive;
        bodyMaterial.depthWrite = !reflectionCaptureActive;
    }, [material]);
    const completeBodyRender = useCallback((
        renderer:WebGLRenderer,
    ) => {
        if(material !== 'glass') return;

        const bodyMaterial = pedestalRef.current?.material as (
            Material | undefined
        );
        if(bodyMaterial) {
            bodyMaterial.colorWrite = true;
            bodyMaterial.depthWrite = true;
        }
        if(renderer.getRenderTarget() !== null) return;

        // The glass barrier is complete only when its transmitted result has
        // reached the default framebuffer, not during an offscreen pass.
        if(pendingTransmissionRevisionRef.current !== undefined) {
            renderReadiness.acknowledge(
                'transmission',
                pendingTransmissionRevisionRef.current,
            );
            pendingTransmissionRevisionRef.current = undefined;
        }
    }, [material]);
    useLayoutEffect(() => {
        pendingTransmissionRevisionRef.current = transmissionRevision;
        if(transmissionRevision !== undefined) {
            renderReadiness.reset(
                'transmission',
                transmissionRevision,
            );
        }
        invalidate();
    }, [invalidate, transmissionRevision]);
    const pedestalMaterial = {
        glass: <PedestalGlassMaterial
            layout={layout}
            parentRef={pedestalRef}
        />,
        marble: <PedestalMarbleMaterial layout={layout} />,
        solid: <meshStandardMaterial
            color={pedestalColor}
            dithering
            metalness={0}
            roughness={0.88}
        />,
    }[material];

    return <>
        {/* Glass and marble need beveled geometry for readable highlights.
        Solid cylinders use the cheaper primitive because their rough finish
        does not expose the edge profile. */}
        {layout.shape === 'cylinder'
            ? <mesh
                ref={pedestalRef}
                castShadow={false}
                frustumCulled={material !== 'glass'}
                onAfterRender={completeBodyRender}
                onBeforeRender={prepareBodyRender}
                position={[0, body.centerY, 0]}
                receiveShadow={false}
            >
                {material === 'glass' || material === 'marble'
                    ? <latheGeometry
                        args={[
                            beveledCylinderProfile,
                            material === 'glass' ? 192 : 128,
                        ]}
                    />
                    : <cylinderGeometry
                        args={[
                            layout.radius,
                            layout.radius,
                            body.height,
                            96,
                        ]}
                    />}
                {pedestalMaterial}
            </mesh>
            : <RoundedBox
                ref={pedestalRef}
                args={[layout.width, body.height, layout.depth]}
                bevelSegments={material === 'solid' ? 4 : 6}
                castShadow={false}
                frustumCulled={material !== 'glass'}
                onAfterRender={completeBodyRender}
                onBeforeRender={prepareBodyRender}
                position={[0, body.centerY, 0]}
                radius={body.boxEdgeRadius}
                receiveShadow={false}
                smoothness={{
                    glass: 8,
                    marble: 6,
                    solid: 4,
                }[material]}
            >
                {pedestalMaterial}
            </RoundedBox>}
        {/* A simplified opaque proxy is visible only to the floor reflector;
        the real transmissive mesh would otherwise contribute weakly. */}
        {material === 'glass'
            ? <PedestalReflectionProxy body={body} layout={layout} />
            : null}
        <PedestalGroundShadow layout={layout} material={material} />
    </>;
});
